import FetchAuditActions from '@Application/AuditTrail/Queries/FetchAuditActions';
import FetchAuditOutcomes from '@Application/AuditTrail/Queries/FetchAuditOutcomes';
import FetchAuditResourceTypes from '@Application/AuditTrail/Queries/FetchAuditResourceTypes';
import fetchAuditActions from '@Application/AuditTrail/fetchAuditActions';
import fetchAuditOutcomes from '@Application/AuditTrail/fetchAuditOutcomes';
import fetchAuditResourceTypes from '@Application/AuditTrail/fetchAuditResourceTypes';
import auditTrailFiltersSignal, {
	setAuditTrailFilters,
} from '@UI/Pages/Administration/AuditTrail/Filters/Signals/auditTrailFiltersSignal';
import type AccountFacilityView from '@UI/Store/AccountFacilities/Views/AccountFacilityView';
import type UserView from '@UI/Store/Users/Views/UserView';
import * as React from 'react';
import FilterPanelSidebar from '@Guidelines/Component/FilterPanel/FilterPanelSidebar';
import FilterPanelTrigger from '@Guidelines/Component/FilterPanel/FilterPanelTrigger';
import useFilterPanel from '@Guidelines/Component/FilterPanel/Hooks/useFilterPanel';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import ActionFilter from '@UI/Pages/Administration/AuditTrail/Filters/ActionFilter';
import ActorFilter from '@UI/Pages/Administration/AuditTrail/Filters/ActorFilter';
import AuditTrailFilterView from '@UI/Pages/Administration/AuditTrail/Filters/AuditTrailFilterView';
import BeneficiaryIdFilter from '@UI/Pages/Administration/AuditTrail/Filters/BeneficiaryIdFilter';
import FacilityFilter from '@UI/Pages/Administration/AuditTrail/Filters/FacilityFilter';
import OutcomeFilter from '@UI/Pages/Administration/AuditTrail/Filters/OutcomeFilter';
import PeriodFilter from '@UI/Pages/Administration/AuditTrail/Filters/PeriodFilter';
import ResourceIdFilter from '@UI/Pages/Administration/AuditTrail/Filters/ResourceIdFilter';
import ResourceTypeFilter from '@UI/Pages/Administration/AuditTrail/Filters/ResourceTypeFilter';
import { Grid, GridItem } from '@verclam/grid-system';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

const PERIOD_COLUMN_SIZE = { xl: 4, lg: 6, md: 6 } as const;
const NARROW_COLUMN_SIZE = { xl: 2, lg: 3, md: 3 } as const;

type Props = {
	/** `null` while `AuditTrailContent`'s own fetch is still resolving. */
	readonly users: readonly UserView[] | null;
	readonly facilities: readonly AccountFacilityView[] | null;
};

/**
 * Hosts the catalog queries and the reset; each field reads and writes `auditTrailFiltersSignal` itself.
 * Every field is a server filter — there is no client-side narrowing on this screen. There is no pinned
 * field: on a compact viewport the row holds only the panel trigger.
 */
const Filters: React.FC<Props> = ({ users, facilities }) => {
	const currentFilters = useSignalValue(auditTrailFiltersSignal);
	const dispatch = useDispatch();
	const { isCompact, isOpen, panelId, open, close } = useFilterPanel();

	// No `cache` preset on any of the three: each `stopOn` reads a store that survives this
	// component unmounting, so every catalog is fetched once per session rather than once per mount.
	const { data: auditActions } = useStoreQuery({
		selector: (state) => state.auditActionsStore.auditActions,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchAuditActions(new FetchAuditActions())),
	});

	const { data: auditOutcomes } = useStoreQuery({
		selector: (state) => state.auditOutcomesStore.auditOutcomes,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchAuditOutcomes(new FetchAuditOutcomes())),
	});

	const { data: auditResourceTypes } = useStoreQuery({
		selector: (state) => state.auditResourceTypesStore.auditResourceTypes,
		stopOn: (value) => value !== null,
		query: () => dispatch(fetchAuditResourceTypes(new FetchAuditResourceTypes())),
	});

	const handleReset = React.useCallback(() => {
		setAuditTrailFilters(AuditTrailFilterView.createDefault());
	}, []);

	return (
		<React.Fragment>
			<Grid columns={12} columnGap={1} rowGap={1} alignItems="end">
				<ConditionalLoader
					data={!isCompact}
					fallback={
						<GridItem columnSize={12} justifySelf="end">
							<FilterPanelTrigger
								panelId={panelId}
								isOpen={isOpen}
								activeCount={currentFilters.activeFilterCount}
								onClick={open}
							/>
						</GridItem>
					}
				>
					<GridItem columnSize={PERIOD_COLUMN_SIZE}>
						<PeriodFilter />
					</GridItem>
					<GridItem columnSize={NARROW_COLUMN_SIZE}>
						<ActorFilter users={users} />
					</GridItem>
					<GridItem columnSize={NARROW_COLUMN_SIZE}>
						<ActionFilter options={auditActions} />
					</GridItem>
					<GridItem columnSize={NARROW_COLUMN_SIZE}>
						<OutcomeFilter options={auditOutcomes} />
					</GridItem>
					<GridItem columnSize={NARROW_COLUMN_SIZE}>
						<ResourceTypeFilter options={auditResourceTypes} />
					</GridItem>
					<GridItem columnSize={NARROW_COLUMN_SIZE}>
						<FacilityFilter facilities={facilities} />
					</GridItem>
					<GridItem columnSize={NARROW_COLUMN_SIZE}>
						<ResourceIdFilter />
					</GridItem>
					<GridItem columnSize={NARROW_COLUMN_SIZE}>
						<BeneficiaryIdFilter />
					</GridItem>
				</ConditionalLoader>
			</Grid>
			<FilterPanelSidebar
				panelId={panelId}
				visible={isOpen}
				activeCount={currentFilters.activeFilterCount}
				onHide={close}
				onReset={handleReset}
			>
				<Grid columns={1} rowGap={1}>
					<GridItem>
						<PeriodFilter withLabel />
					</GridItem>
					<GridItem>
						<ActorFilter users={users} withLabel />
					</GridItem>
					<GridItem>
						<ActionFilter options={auditActions} withLabel />
					</GridItem>
					<GridItem>
						<OutcomeFilter options={auditOutcomes} withLabel />
					</GridItem>
					<GridItem>
						<ResourceTypeFilter options={auditResourceTypes} withLabel />
					</GridItem>
					<GridItem>
						<FacilityFilter facilities={facilities} withLabel />
					</GridItem>
					<GridItem>
						<ResourceIdFilter withLabel />
					</GridItem>
					<GridItem>
						<BeneficiaryIdFilter withLabel />
					</GridItem>
				</Grid>
			</FilterPanelSidebar>
		</React.Fragment>
	);
};

export default Filters;
